/**
 * Jamaica Schools Guide - Educational Data Module
 *
 * Unified export of all educational datasets for Jamaica:
 * - Public Secondary Schools
 * - Independent Secondary Schools
 * - CSEC Subjects
 * - CAPE Subjects
 * - School Performance Rankings (2024)
 *
 * Sources:
 * - Ministry of Education & Youth Directory 2018-19
 * - CXC Syllabus Downloads
 * - Educate Jamaica High School Performance Index 2024
 */

import publicSchoolsData from './schools-public.json';
import independentSchoolsData from './schools-independent.json';
import csecSubjectsData from './csec-subjects.json';
import capeSubjectsData from './cape-subjects.json';
import rankingsData from './school-rankings-2024.json';

// ============================================
// SCHOOLS DATA
// ============================================

/**
 * All public secondary schools in Jamaica
 * @type {Array<{school_name: string, parish: string, school_type: string, sector: string}>}
 */
export const publicSchools = publicSchoolsData.data;

/**
 * All independent/private secondary schools in Jamaica
 * @type {Array<{school_name: string, parish: string, school_type: string, sector: string}>}
 */
export const independentSchools = independentSchoolsData.data;

/**
 * Combined list of all secondary schools (public + independent)
 */
export const allSchools = [...publicSchools, ...independentSchools];

/**
 * Schools grouped by parish
 */
export const schoolsByParish = allSchools.reduce((acc, school) => {
  const parish = school.parish;
  if (!acc[parish]) {
    acc[parish] = [];
  }
  acc[parish].push(school);
  return acc;
}, {});

/**
 * Schools grouped by type (Secondary High, Technical High)
 */
export const schoolsByType = allSchools.reduce((acc, school) => {
  const type = school.school_type;
  if (!acc[type]) {
    acc[type] = [];
  }
  acc[type].push(school);
  return acc;
}, {});

/**
 * Schools grouped by sector (Public, Independent)
 */
export const schoolsBySector = {
  Public: publicSchools,
  Independent: independentSchools
};

// ============================================
// EXAM SUBJECTS DATA
// ============================================

/**
 * CSEC (Caribbean Secondary Education Certificate) subjects
 * @type {Array<{subject: string, level: string, category: string}>}
 */
export const csecSubjects = csecSubjectsData.data;

/**
 * CAPE (Caribbean Advanced Proficiency Examination) subjects
 * @type {Array<{subject: string, level: string, category: string, units: number}>}
 */
export const capeSubjects = capeSubjectsData.data;

/**
 * CSEC subjects grouped by category
 */
export const csecByCategory = csecSubjects.reduce((acc, subject) => {
  const category = subject.category;
  if (!acc[category]) {
    acc[category] = [];
  }
  acc[category].push(subject);
  return acc;
}, {});

/**
 * CAPE subjects grouped by category
 */
export const capeByCategory = capeSubjects.reduce((acc, subject) => {
  const category = subject.category;
  if (!acc[category]) {
    acc[category] = [];
  }
  acc[category].push(subject);
  return acc;
}, {});

/**
 * All exam subject categories
 */
export const subjectCategories = [
  'Sciences',
  'Mathematics',
  'Languages',
  'Humanities',
  'Business',
  'Technology',
  'Technical/Vocational',
  'Creative Arts',
  'Physical Education'
];

// ============================================
// SCHOOL RANKINGS DATA
// ============================================

/**
 * School performance rankings for 2024 (Educate Jamaica High School Performance Index)
 * @type {Array<{rank: number, school_name: string, gender: string, parish: string, score: number, score_2023: number|null, change: string|null, trend: string, year: number}>}
 */
export const schoolRankings2024 = rankingsData.data;

/**
 * Top 10 performing schools
 */
export const topTenSchools = schoolRankings2024.slice(0, 10);

/**
 * Rankings grouped by parish
 */
export const rankingsByParish = schoolRankings2024.reduce((acc, school) => {
  const parish = school.parish;
  if (!acc[parish]) {
    acc[parish] = [];
  }
  acc[parish].push(school);
  return acc;
}, {});

/**
 * Rankings grouped by gender type
 */
export const rankingsByGender = schoolRankings2024.reduce((acc, school) => {
  const gender = school.gender;
  if (!acc[gender]) {
    acc[gender] = [];
  }
  acc[gender].push(school);
  return acc;
}, {});

// ============================================
// STATISTICS & METADATA
// ============================================

/**
 * Summary statistics for all educational data
 */
export const educationStats = {
  totalPublicSchools: publicSchools.length,
  totalIndependentSchools: independentSchools.length,
  totalSchools: allSchools.length,
  totalCSECSubjects: csecSubjects.length,
  totalCAPESubjects: capeSubjects.length,
  rankedSchools: schoolRankings2024.length,
  dataYear: 2024,
  parishes: [...new Set(allSchools.map(s => s.parish))].sort()
};

/**
 * Jamaica's 14 parishes
 */
export const PARISHES = [
  'Kingston',
  'St. Andrew',
  'St. Thomas',
  'Portland',
  'St. Mary',
  'St. Ann',
  'Trelawny',
  'St. James',
  'Hanover',
  'Westmoreland',
  'St. Elizabeth',
  'Manchester',
  'Clarendon',
  'St. Catherine'
];

/**
 * School types available
 */
export const SCHOOL_TYPES = [
  'Secondary High',
  'Technical High'
];

/**
 * School sectors/ownership types
 */
export const SCHOOL_SECTORS = [
  'Public',
  'Independent'
];

/**
 * Exam types in Jamaica
 */
export const EXAM_TYPES = [
  { id: 'pep', name: 'PEP', fullName: 'Primary Exit Profile' },
  { id: 'csec', name: 'CSEC', fullName: 'Caribbean Secondary Education Certificate' },
  { id: 'cape', name: 'CAPE', fullName: 'Caribbean Advanced Proficiency Examination' },
  { id: 'nvqj', name: 'NVQ-J', fullName: 'National Vocational Qualification of Jamaica' }
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Search schools by name
 * @param {string} query - Search query
 * @returns {Array} Matching schools
 */
export const searchSchools = (query) => {
  const lowerQuery = query.toLowerCase();
  return allSchools.filter(school =>
    school.school_name.toLowerCase().includes(lowerQuery)
  );
};

/**
 * Filter schools by multiple criteria
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered schools
 */
export const filterSchools = (filters = {}) => {
  let result = [...allSchools];

  if (filters.parish) {
    result = result.filter(s => s.parish === filters.parish);
  }
  if (filters.sector) {
    result = result.filter(s => s.sector === filters.sector);
  }
  if (filters.school_type) {
    result = result.filter(s => s.school_type === filters.school_type);
  }
  if (filters.search) {
    const query = filters.search.toLowerCase();
    result = result.filter(s => s.school_name.toLowerCase().includes(query));
  }

  return result;
};

/**
 * Get schools in a specific parish
 * @param {string} parish - Parish name
 * @returns {Array} Schools in that parish
 */
export const getSchoolsInParish = (parish) => {
  return allSchools.filter(school => school.parish === parish);
};

/**
 * Get school ranking by name
 * @param {string} schoolName - School name to search
 * @returns {Object|null} Ranking data or null
 */
export const getSchoolRanking = (schoolName) => {
  const lowerName = schoolName.toLowerCase();
  return schoolRankings2024.find(school =>
    school.school_name.toLowerCase().includes(lowerName)
  ) || null;
};

/**
 * Get subjects by category
 * @param {string} examType - 'csec' or 'cape'
 * @param {string} category - Subject category
 * @returns {Array} Subjects in that category
 */
export const getSubjectsByCategory = (examType, category) => {
  const subjects = examType === 'csec' ? csecSubjects : capeSubjects;
  return subjects.filter(s => s.category === category);
};

// Default export with all data
export default {
  // Schools
  publicSchools,
  independentSchools,
  allSchools,
  schoolsByParish,
  schoolsByType,
  schoolsBySector,

  // Subjects
  csecSubjects,
  capeSubjects,
  csecByCategory,
  capeByCategory,
  subjectCategories,

  // Rankings
  schoolRankings2024,
  topTenSchools,
  rankingsByParish,
  rankingsByGender,

  // Stats & Constants
  educationStats,
  PARISHES,
  SCHOOL_TYPES,
  SCHOOL_SECTORS,
  EXAM_TYPES,

  // Helper functions
  searchSchools,
  filterSchools,
  getSchoolsInParish,
  getSchoolRanking,
  getSubjectsByCategory
};
